import { Circle as CircleStyle, Fill, RegularShape, Stroke, Style, Text } from "ol/style";
import { MultiPoint, LineString, Polygon } from "ol/geom";
import { DrawType } from "../enums";
import { Feature } from "ol";
import { IIdentifyStyles } from "../IUserConfig";
import { FeatureLike } from "ol/Feature";
import { addMeasurements } from "./measureSymbols";

const yellow = [255, 255, 0, 0.8];

const vertice = (color) =>
    new CircleStyle({
        radius: 6,
        fill: new Fill({
            color
        }),
        stroke: new Stroke({
            color: "white",
            width: 1.5
        })
    });

const verticeStyle = (color: string) =>
    new Style({
        image: vertice(color)
    });

const fill = (color: string) =>
    new Fill({
        color: hex2rgba(color, 0.2)
    });

const fillStyle = (color: string, dash?: boolean) =>
    new Style({
        fill: fill(color),
        stroke: stroke(color, dash)
    });

const stroke = (color: string, dash?: boolean) =>
    new Stroke({
        color,
        width: 3,
        lineDash: dash ? [10, 10] : null
    });

const lineStyle = (color: string, dash?: boolean) => new Style({ stroke: stroke(color, dash) });

const middlePointsStyle = (color: string, drawType?: DrawType.Polygon | DrawType.Polyline) =>
    new Style({
        image: vertice(color),
        geometry: (feature) => {
            if (drawType === DrawType.Polyline) {
                return new MultiPoint(((feature as Feature).getGeometry() as LineString).getCoordinates());
            }
            const coords = (feature.getGeometry() as Polygon).getCoordinates();
            return new MultiPoint(coords.length === 1 ? coords[0] : (coords as []));
        }
    });

const pointStyle = (color: string, dash: boolean) =>
    new Style({
        image: new CircleStyle({
            fill: new Fill({ color: "white" }),
            stroke: new Stroke({
                color,
                width: dash ? 2 : 3,
                lineDash: dash ? [3, 3] : null
            }),
            radius: dash ? 6 : 5
        })
    });

const pointLabelStyle = (text: string, color) =>
    new Style({
        text: new Text({
            font: 'normal normal bold 14px "Mulish"',
            text,
            fill: new Fill({ color: "white" }),
            stroke: stroke(color),
            backgroundFill: fill(color),
            backgroundStroke: new Stroke({
                color,
                width: 2,
                lineDash: [10, 10]
            }),
            padding: [3, 3, 3, 3],
            textBaseline: "middle"
        })
    });

const textLabelStyle = (color: string, text: string, editMode: boolean) =>
    editMode
        ? pointLabelStyle(text, color)
        : new Style({
              text: new Text({
                  font: 'normal normal bold 14px "Mulish"',
                  text,
                  textBaseline: "middle",
                  fill: new Fill({ color }),
                  stroke: stroke("white")
              })
          });

const hex2rgba = (hex: string, alpha = 1): number[] => {
    const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));
    return [r, g, b, alpha];
};

export const getDrawingSymbol = (
    feature: Feature | FeatureLike,
    noMeasurements: boolean,
    type: DrawType,
    color?: string,
    isSelect?: boolean,
    isModify?: boolean
): Style | Style[] => {
    const styles: Style[] = [];
    if (type === DrawType.Polygon) {
        if (isModify) {
            styles.push(verticeStyle(color));
        } else if (isSelect) {
            styles.push(verticeStyle(color), fillStyle(color, true), middlePointsStyle(color, type));
        } else {
            styles.push(fillStyle(color));
        }
    } else if (type === DrawType.Polyline) {
        if (isModify) {
            styles.push(verticeStyle(color));
        } else if (isSelect) {
            styles.push(verticeStyle(color), lineStyle(color, true), middlePointsStyle(color, type));
        } else {
            styles.push(lineStyle(color));
        }
    } else if (type === DrawType.PointText) {
        const description = feature.get("DESCRIPTION");
        const text = description ? description.toString() : "";
        (isModify || isSelect) && !text.length ? styles.push(pointStyle(color, false)) : styles.push(new Style());
        !noMeasurements && styles.push(textLabelStyle(color, text, isModify || isSelect));
    } else {
        styles.push(pointStyle(color, isModify || isSelect));
    }

    return noMeasurements || type === DrawType.PointText ? styles : addMeasurements(feature, styles);
};

export const getHighLightSymbol = (feature: Feature, styles: IIdentifyStyles): Style => {
    const type = feature.getGeometry().getType();

    if (type === "Polygon" || type === "MultiPolygon") {
        return new Style({
            fill: new Fill({
                color: styles?.polygon?.color || [255, 255, 0, 0.15]
            }),
            stroke: new Stroke({
                color: styles?.polygon?.outline?.color || yellow,
                width: styles?.polygon?.outline?.width || 3
            })
        });
    } else if (type === "LineString" || type === "MultiLineString") {
        return new Style({
            stroke: new Stroke({
                color: styles?.polyline?.color || yellow,
                width: styles?.polyline?.width || 5
                // lineDash: isNaturalRoad && [4, 4]
            })
        });
    } else if (type === "Point") {
        return new Style({
            image: new CircleStyle({
                fill: new Fill({
                    color: styles?.point?.color || "white"
                }),
                stroke: new Stroke({
                    color: styles?.point?.outline?.color || yellow,
                    width: styles?.point?.outline?.width || 2
                }),
                radius: styles?.point?.size || 12
            })
        });
    }
};
